/**
 * Opening a connection: the two REST calls and the Colyseus join behind them.
 *
 * The socket URL is *derived*, never written down. `docs/KNOWN-BUGS.md` item
 * 6: "An `https` page cannot open a `ws://` socket. Always `wss://`, same
 * origin as the client." `@interlock/protocol`'s `socketEndpoint` is the one
 * function that turns a `location` into an endpoint, and this module is the
 * only caller — so there is exactly one place in the client that decides where
 * the server is, and it decides by looking at where the page came from.
 *
 * The REST calls go to relative paths for the same reason: `fetch('/api/...')`
 * resolves against the page, so the dev server, the dev host and production
 * all work without a build-time constant.
 */
import { API_ROOMS, apiRoomByCode, socketEndpoint } from '@interlock/protocol';
import type { AiLevel, DuelRules, OriginLike } from '@interlock/protocol';
import { Client } from 'colyseus.js';
import type { Room } from 'colyseus.js';

import { socketTicket } from '../state/account.svelte.js';
import { DuelClient } from './duel-client.js';

function origin(): OriginLike {
  return { protocol: window.location.protocol, host: window.location.host };
}

/** The WebSocket endpoint for this page. */
function endpointForThisPage(): string {
  return socketEndpoint(origin());
}

export interface CreatedRoom {
  readonly code: string;
  readonly roomId: string;
  /** What the server actually created, after validating what was asked for. */
  readonly rules: DuelRules;
  /** The difficulty the server seated its own opponent at, or `null` for a room for two people. */
  readonly ai: AiLevel | null;
}

/**
 * Ask the server for a room, playing the duel the creator picked.
 *
 * The code is the server's to invent, never the client's — and so, in the end,
 * are the rules: what is posted here is a *request*, and `parseDuelRules` on
 * the other side decides what the room actually plays. The response echoes the
 * result rather than the request, so a screen never shows a configuration the
 * room is not running.
 *
 * `handicap` is the room option `docs/prompts/07-specials.md` asks for: "on
 * by default in friendly, display in lobby, creator can turn it off."
 * Omitted, the server picks its own default (friendly: on); named, it is
 * exactly what the creator chose.
 *
 * `ai` is the "Play the computer" path: the server seats its own
 * opponent in seat 1 at that difficulty, so the room is playable the instant
 * the creator walks into it — no code to read out and nobody to wait for. It
 * also turns the handicap off, which the server enforces rather than trusting
 * this call to: difficulty *is* the handicap.
 */
export async function createRoom(
  rules: DuelRules,
  handicap?: boolean,
  ai?: AiLevel,
): Promise<CreatedRoom> {
  const response = await fetch(API_ROOMS, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      rules,
      ...(handicap === undefined ? {} : { handicap }),
      ...(ai === undefined ? {} : { ai }),
    }),
  });
  if (!response.ok) throw new Error('Could not create a room. Is the server reachable?');
  return (await response.json()) as CreatedRoom;
}

/** Resolve a typed room code. `null` means no room has that code. */
export async function findRoom(code: string): Promise<string | null> {
  const response = await fetch(apiRoomByCode(code));
  if (response.status === 404) return null;
  if (!response.ok) throw new Error('Could not reach the server.');
  return ((await response.json()) as { roomId: string }).roomId;
}

export interface JoinRequest {
  readonly roomId: string;
  readonly name: string;
  readonly role: 'player' | 'spectator';
}

/**
 * The account token a join may carry, or `undefined`.
 *
 * `docs/DESIGN.md`'s accounts amendment: "room join options may carry the
 * session token so the server can attribute seats to players". It cannot carry
 * the session *cookie* — that is `httpOnly` and unreadable here on purpose —
 * so what is fetched is a single-use, sixty-second socket ticket
 * (`state/account-io.ts`, and `docs/decisions/0028`).
 *
 * Every failure is `undefined` and every `undefined` is a guest seat. That is
 * the shape the whole feature has: a duel that could not start because an
 * optional statistic could not be attributed would be exactly the degradation
 * the amendment's first sentence forbids.
 */
async function accountToken(): Promise<string | undefined> {
  try {
    return (await socketTicket()) ?? undefined;
  } catch {
    return undefined;
  }
}

export async function joinRoom(request: JoinRequest): Promise<Room> {
  const ticket = await accountToken();
  const client = new Client(endpointForThisPage());
  return client.joinById(
    request.roomId,
    DuelClient.joinOptions(request.name, request.role, undefined, ticket),
  );
}

export interface RejoinRequest extends JoinRequest {
  /** The room code, so the room can be found again if its id changed. */
  readonly code: string;
  /** Colyseus's own token for the seat the room is still holding, when there is one. */
  readonly reconnectionToken: string;
  /** Interlock's token for the seat, which outlives the process that minted it. */
  readonly sessionToken: string;
}

/**
 * Get back into a room after the socket died. Two paths, tried in order,
 * because they fail in different ways.
 *
 * **The transport's reconnection token** is the fast path and covers the
 * ordinary case: the room is still running, the seat is still reserved, and
 * `reconnect` reattaches to it without a join happening at all. It is worthless
 * if the process restarted, because the reservation it names lived in memory.
 *
 * **The session token** is the slow path and covers exactly that: look the room
 * up by its code — a restored room re-binds the same code, so the lookup is the
 * one the lobby already does — and join it presenting the token the seat is
 * held under. `docs/DESIGN.md`: "Rejoin uses a session token."
 *
 * Trying the fast one first matters for more than speed: a `joinById` while a
 * reservation is still outstanding would be refused as a full room, so the
 * order is what makes the common case work at all.
 */
export async function rejoinRoom(request: RejoinRequest): Promise<Room> {
  const client = new Client(endpointForThisPage());

  if (request.reconnectionToken !== '') {
    try {
      return await client.reconnect(request.reconnectionToken);
    } catch {
      // Expired, consumed, or the room it named is gone. Fall through.
    }
  }

  const roomId = (await findRoom(request.code)) ?? request.roomId;
  // A fresh ticket, because the one the first join spent is gone — they are
  // single-use. A rejoin that cannot get one still rejoins: the *seat* is
  // proved by `sessionToken`, and the account only decides whose record the
  // finished match lands on.
  const ticket = await accountToken();
  return client.joinById(
    roomId,
    DuelClient.joinOptions(request.name, request.role, request.sessionToken, ticket),
  );
}
